/**
 * Rank 1 against rank 2 of **this run**, as a head-to-head.
 *
 * The one place in this UI where a versus framing is honest. These two hypotheses
 * were in the same tournament: the ranker played them against each other, and the
 * Bradley-Terry fit over those matches is what `btl_score` is. So `btl₁ / (btl₁ +
 * btl₂)` is the model's own implied probability that the leader wins the pairing —
 * a quantity that exists, not a ratio invented for a graphic.
 *
 * On the cumulative board the same picture would be a lie, which is why it is not
 * there: two board rows can come from different runs, whose BTL fits are over
 * different pools with no shared opponent, so their values are not comparable and a
 * divided bar would assert a match that was never played.
 *
 * ── What it refuses to do ───────────────────────────────────────────────────────
 *
 * **It will not promote a row to "the leader".** It renders the rows whose `rank`
 * is literally 1 and 2. A reader can sort this table by protein or page past the top,
 * and picking "the first row on screen" would then caption an arbitrary target as the
 * run's leading one. If rank 1 is not among the loaded rows there is no band.
 *
 * **A one-target run degrades to a single leading target, not a versus with an empty
 * side.** An opponent-shaped void reads as an opponent that lost badly.
 *
 * **A missing `btl_score` is a named gap, not a 50/50 bar.** A bar drawn from a
 * missing number is indistinguishable from a genuine dead heat.
 */
import { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader } from './ui/Card'
import { Chip } from './ui/Chip'
import { TierBadge } from './StatusBadge'
import { formatNumber } from '../lib/utils'
import type { Target } from '../api/types'

/**
 * `btl_score` as a number, or `null` if there isn't one.
 *
 * `number | string` is not a schema bug: `btl_score` is a Postgres `numeric`, psycopg
 * hands it over as a `Decimal` and the API stringifies it rather than lose precision.
 * Both forms arrive at runtime whatever the type says, so this coerces rather than
 * trusting either.
 */
function btlOf(t: Target | undefined): number | null {
  const raw = t?.btl_score
  if (raw === null || raw === undefined || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

export function LeaderBand({ targets }: { targets: Target[] }) {
  const leader = targets.find((t) => t.rank === 1)
  const runnerUp = targets.find((t) => t.rank === 2)

  const leaderBtl = btlOf(leader)
  const runnerBtl = btlOf(runnerUp)
  const pairTotal =
    leaderBtl !== null && runnerBtl !== null ? leaderBtl + runnerBtl : 0
  // Only when both scores exist and their sum is positive. A zero sum is possible in
  // principle and would divide by nothing.
  const share = pairTotal > 0 ? ((leaderBtl as number) / pairTotal) * 100 : null

  // The split animates from an even 50/50 to the real ratio once, on first paint,
  // which is what makes the asymmetry read as a result rather than as a layout.
  // State starting at 50 plus a `useEffect` — deliberately not `useLayoutEffect`,
  // which would apply the real width before the browser had painted the even one and
  // so animate nothing. The reduced-motion rule in index.css cuts the transition
  // globally; there is nothing to opt out of here.
  const [barWidth, setBarWidth] = useState(50)
  useEffect(() => {
    if (share !== null) setBarWidth(share)
  }, [share])

  if (!leader) return null

  return (
    <Card className="animate-rise">
      <CardHeader
        eyebrow="This run's tournament"
        title={runnerUp ? 'Head to head' : 'Leading target'}
        description={
          runnerUp
            ? 'The two top-ranked hypotheses of this run, which competed in the same tournament. The split is the Bradley-Terry fit over those matches — it says nothing about any other run.'
            : 'This run ranked one target, so there was no pairing to fit. A single hypothesis is not a tournament result.'
        }
      />
      <CardBody className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <Side target={leader} />
          {runnerUp && <Side target={runnerUp} align="right" />}
        </div>

        {runnerUp && (
          <div className="space-y-1.5">
            {share !== null ? (
              <>
                <div
                  className="flex h-2.5 w-full overflow-hidden rounded-full bg-slate-800"
                  role="img"
                  aria-label={`Bradley-Terry split: ${leader.protein} ${Math.round(
                    share,
                  )}%, ${runnerUp.protein} ${100 - Math.round(share)}%.`}
                >
                  <div
                    className="bg-brand-500 transition-[width] duration-500 ease-out"
                    style={{ width: `${barWidth}%` }}
                  />
                  <div className="flex-1 bg-slate-600" />
                </div>
                <div className="flex items-baseline justify-between gap-4 text-2xs">
                  <span className="font-mono tabular-nums text-brand-300">
                    {share.toFixed(1)}%
                  </span>
                  <span
                    className="min-w-0 truncate px-2 text-center text-slate-500"
                    title="Under the Bradley-Terry model fitted to this tournament's pairwise matches, the share is the probability the leader wins the pairing. It is a fit over one run's matches, not a measurement of either protein."
                  >
                    the fit gives {leader.protein} this pairing about{' '}
                    {Math.round(share)}% of the time
                  </span>
                  <span className="font-mono tabular-nums text-slate-400">
                    {(100 - share).toFixed(1)}%
                  </span>
                </div>
              </>
            ) : (
              // A named gap, not a bar. One of the two carries no Bradley-Terry
              // score — a row can be ranked without ever playing a scored match —
              // and a 50/50 split would render that as a dead heat.
              <p className="text-2xs text-slate-500">
                No Bradley-Terry split to draw:{' '}
                {leaderBtl === null && runnerBtl === null
                  ? 'neither row carries a score'
                  : leaderBtl === null
                    ? `${leader.protein} carries no score`
                    : `${runnerUp.protein} carries no score`}
                . A row can be ranked without having played a scored match, and an
                even bar would read as a tie.
              </p>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/** One competitor: the name, what identifies it, and its two numbers. */
function Side({
  target,
  align = 'left',
}: {
  target: Target
  align?: 'left' | 'right'
}) {
  const right = align === 'right'
  return (
    <div className={`min-w-0 max-w-[46%] ${right ? 'text-right' : ''}`}>
      <p className="font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
        Rank {target.rank}
      </p>
      <h3 className="mt-0.5 truncate font-display text-lg font-semibold text-slate-100">
        {target.protein}
      </h3>
      <div
        className={`mt-1.5 flex flex-wrap items-center gap-2 ${
          right ? 'justify-end' : ''
        }`}
      >
        {/* No accession is an answer about the row, not about the protein: nothing
            could be searched for it, so it is said rather than left blank. */}
        {target.accession ? (
          <Chip font="mono">{target.accession}</Chip>
        ) : (
          <Chip>No accession recorded</Chip>
        )}
        <TierBadge tier={target.tier} />
      </div>
      <p className="mt-2 text-2xs text-slate-500">
        <span
          className="font-mono tabular-nums text-slate-300"
          title="Elo from this run's tournament. It starts at 1500 with K=24 and is unbounded, so it has no maximum to draw a bar against — and it is a property of this tournament's pool, not of the protein."
        >
          {formatNumber(target.elo)}
        </span>{' '}
        Elo
      </p>
    </div>
  )
}
